import { useEffect, useState } from "react";
import { ChartLineUp } from "@phosphor-icons/react";
import { EmptyState } from "../components/shared/EmptyState";
import { GameSearchBar } from "../components/line-movement/GameSearchBar";
import { LeagueFilterBar } from "../components/line-movement/LeagueFilterBar";
import { MovementFilters } from "../components/line-movement/MovementFilters";
import { MovementTable } from "../components/line-movement/MovementTable";
import { getLineMovement, getLineMovementLeagues } from "../lib/api";

const matchesSearch = (row, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [row.away_team, row.home_team, row.league, `${row.away_team} @ ${row.home_team}`, `${row.away_team} vs ${row.home_team}`]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
};

const LineMovement = () => {
  const [leagues, setLeagues] = useState([]);
  const [league, setLeague] = useState("ALL");
  const [minMovement, setMinMovement] = useState(0);
  const [sort, setSort] = useState("movement");
  const [moversOnly, setMoversOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getLineMovementLeagues()
      .then((d) => setLeagues(d.leagues))
      .catch(() => setLeagues([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    // "Biggest Movers" is UI/query logic only -- it reuses the same already-returned movement
    // data (always sorted largest-absolute-movement-first by the existing "movement" sort),
    // just filtered client-side to drop 0.0/undetermined rows. No new endpoint, no change to
    // polling/consensus/tracked_open.
    getLineMovement({ league, min_movement: moversOnly ? 0 : minMovement, sort: moversOnly ? "movement" : sort })
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [league, minMovement, sort, moversOnly]);

  const rows = data?.picks || [];
  // Search composes with whatever's already loaded -- purely client-side over the same
  // already-fetched rows, never a new request per keystroke.
  const searchedRows = search.trim() ? rows.filter((r) => matchesSearch(r, search)) : rows;
  const displayRows = moversOnly ? searchedRows.filter((r) => r.movement !== null && r.movement !== 0) : searchedRows;
  const noSearchMatch = search.trim() && rows.length > 0 && displayRows.length === 0;

  return (
    <div data-testid="line-movement-page" className="mx-auto flex max-w-[1440px] flex-col gap-6 px-4 py-8 sm:px-6">
      <div>
        <h1 className="font-heading text-3xl uppercase tracking-tight text-white">Line Movement</h1>
        <p className="mt-1 font-mono text-xs text-zinc-500">
          Pregame main totals tracked across sportsbooks over time -- market intelligence only. Never feeds EdgeBall's
          own predictions, Full Game O/U, F5 O/U, NRFI/YRFI, or Largest Leans qualification. "Tracked Open" is the earliest
          total EdgeBall itself observed for a game, not a claim of the true opening market line.
        </p>
      </div>

      <GameSearchBar value={search} onChange={setSearch} />
      <LeagueFilterBar leagues={leagues} active={league} onChange={setLeague} />
      <MovementFilters
        moversOnly={moversOnly}
        onMoversOnlyChange={setMoversOnly}
        minMovement={minMovement}
        onMinMovementChange={(v) => {
          setMoversOnly(false);
          setMinMovement(v);
        }}
        sort={sort}
        onSortChange={setSort}
      />

      {loading ? (
        <p data-testid="line-movement-loading" className="font-mono text-xs text-zinc-500">Loading tracked lines...</p>
      ) : !data ? (
        <EmptyState icon={ChartLineUp} title="Line Movement Unavailable" description="Could not load tracked pregame totals." testId="line-movement-empty" />
      ) : noSearchMatch ? (
        <EmptyState icon={ChartLineUp} title="No Matching Games" description="No loaded games match that search." testId="line-movement-no-search-match" />
      ) : moversOnly && displayRows.length === 0 ? (
        <EmptyState icon={ChartLineUp} title="No Movers Yet" description="No tracked totals have moved yet." testId="line-movement-no-movers" />
      ) : (
        <MovementTable rows={displayRows} />
      )}
    </div>
  );
};

export default LineMovement;

